import React, { useEffect } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Slider,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import { CameraAlt, PermIdentityOutlined } from "@mui/icons-material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import Button from "../../common/Button";
import { tokens } from "../../../theme";
import { useModalStyles } from "../../common/useModalStyles";
import { Image as ImageIcon } from "lucide-react";


type Props = {
  value?: File | null;
  onChange?: (file: File | null) => void;
  allowedTypes?: string[];
  maxSizeMB?: number;
  size?: number;
  disabled?: boolean;
  helperText?: string;
  errorText?: string;
  hideHelper?: boolean;
  id?: string;
  name?: string;
  /** keep aspect support (1 by default) */
  aspect?: number;
  outputType?: "image/jpeg" | "image/png";
  jpegQuality?: number;
  /** output edge size (px) for square/circle) */
  exportSize?: number;
  title?: React.ReactNode;
  titleVariant?: import("@mui/material").TypographyProps["variant"];
  titleAlign?: "left" | "center" | "right";
  /** initial image URL (eg. data URL from server) */
  initialSrc?: string;
  onRemove?: () => void;
  onSave?: (file: File) => void;

  /** NEW: controls mask shape for preview + crop viewport */
  shape?: "circle" | "square";
  showBorder?: boolean;
  preventImmediateClearOnRemove?: boolean;
};

const defaultTypes = ["image/jpeg", "image/jpg", "image/png"];

const ProfileImageUpload: React.FC<Props> = ({
  value = null,
  onChange,
  allowedTypes = defaultTypes,
  maxSizeMB = 3,
  size = 160,
  disabled,
  helperText,
  errorText,
  hideHelper,
  id = "profile-image-upload",
  name = "profile-image-upload",
  aspect = 1,
  outputType,
  jpegQuality = 0.92,
  exportSize = 512,
  title,
  titleVariant = "subtitle1",
  titleAlign = "center",
  initialSrc,
  onRemove,
  shape = "circle",
  onSave,
  showBorder = true,
  preventImmediateClearOnRemove = false,
}) => {
  const {
  paperSx,
  headerSx,
  bodySx,
  footerSx,
  tokens: modalTokens,
  iconBoxSx,
  primaryBtnSx,
  cancelBtnSx,
} = useModalStyles();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // All circle/square places use this radius
  const br = shape === "circle" ? "50%" : 2;

  // preview shows either initialSrc (from server) or picked file
  const [preview, setPreview] = React.useState<string | null>(
    initialSrc ?? null
  );
  // when user presses ❌ we mark cleared to avoid restoring initialSrc automatically
  const [cleared, setCleared] = React.useState(false);

  const [internalError, setInternalError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // Crop dialog state
  const [cropOpen, setCropOpen] = React.useState(false);
  const [rawObjectUrl, setRawObjectUrl] = React.useState<string | null>(null);
  const [lastPickedFile, setLastPickedFile] = React.useState<File | null>(null);

  // Image info
  const [imgEl, setImgEl] = React.useState<HTMLImageElement | null>(null);
  const [imgNatural, setImgNatural] = React.useState<{
    w: number;
    h: number;
  } | null>(null);

  // Pan/zoom
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState<{ x: number; y: number }>({
    x: 0,
    y: 0,
  });
  const [isDragging, setIsDragging] = React.useState(false);
  const viewportRef = React.useRef<HTMLDivElement | null>(null);
  const [vpD, setVpD] = React.useState(320);

  const clampZoom = (z: number) => Math.min(5, Math.max(1, z));
  const ZOOM_STEP = 0.1;

  type PointLike = { clientX: number; clientY: number };
  const lastTouches = React.useRef<{
    dist?: number;
    ox?: number;
    oy?: number;
  } | null>(null);
  const distance = (t1: PointLike, t2: PointLike) =>
    Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);

  const dragRef = React.useRef<{
    startX: number;
    startY: number;
    startOffsetX: number;
    startOffsetY: number;
  } | null>(null);

  const acceptAttr = allowedTypes
    .map((t) => {
      if (t.includes("jpeg") || t.includes("jpg")) return ".jpeg,.jpg";
      if (t.includes("png")) return ".png";
      return "";
    })
    .filter(Boolean)
    .join(",");

  // Sync external value or initialSrc unless user explicitly cleared
  useEffect(() => {
    if (value) {
      const url = URL.createObjectURL(value);
      setPreview(url);
      setCleared(false);
      return () => URL.revokeObjectURL(url);
    } else {
      if (!cleared) {
        setPreview(initialSrc ?? null);
      } else {
        setPreview(null);
      }
    }
  }, [value, initialSrc, cleared]);

  const validate = (file: File) => {
    if (file.size > maxSizeMB * 1024 * 1024)
      return `File is too large. Max size is ${maxSizeMB} MB.`;
    const ok =
      allowedTypes.some((t) =>
        t.includes("/")
          ? file.type === t
          : file.name.toLowerCase().endsWith(t.replace(".", ""))
      ) || allowedTypes.some((t) => file.type === t);
    if (!ok) return "Unsupported file type.";
    return null;
  };

  const handlePick = () => {
    if (disabled) return;
    if (inputRef.current) inputRef.current.value = "";
    inputRef.current?.click();
  };

  const handleChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const err = validate(file);
    if (err) {
      setInternalError(err);
      onChange?.(null);
      e.currentTarget.value = "";
      return;
    }

    setInternalError(null);
    setLastPickedFile(file);

    const url = URL.createObjectURL(file);
    setRawObjectUrl(url);

    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setCropOpen(true);
  };

  // Remove (don’t open picker)
  const clear = () => {
    setInternalError(null);
    setCleared(true);
    setPreview(null);
    onChange?.(null);
    if (inputRef.current) inputRef.current.value = "";
    onRemove?.();
  };

  // Load raw image into crop dialog
  useEffect(() => {
    if (!rawObjectUrl) return;
    const img = new Image();
    img.onload = () => {
      setImgEl(img);
      setImgNatural({ w: img.naturalWidth, h: img.naturalHeight });
      setZoom(1);
      setOffset({ x: 0, y: 0 });
    };
    img.onerror = () => setInternalError("Failed to load image.");
    img.src = rawObjectUrl;
    return () => {
      setImgEl(null);
      setImgNatural(null);
    };
  }, [rawObjectUrl]);

  const clampOffset = React.useCallback(
    (ofs: { x: number; y: number }, d: number) => {
      if (!imgNatural) return ofs;
      const { w, h } = imgNatural;
      const baseScale = Math.max(d / w, d / h);
      const totalScale = baseScale * zoom;
      const dispW = w * totalScale;
      const dispH = h * totalScale;
      const maxX = Math.max(0, (dispW - d) / 2);
      const maxY = Math.max(0, (dispH - d) / 2);
      return {
        x: Math.min(Math.max(ofs.x, -maxX), maxX),
        y: Math.min(Math.max(ofs.y, -maxY), maxY),
      };
    },
    [imgNatural, zoom]
  );

  // Observe viewport width for a crisp circle/square
  useEffect(() => {
    if (!cropOpen) return;
    const el = viewportRef.current;
    if (!el) return;

    const measure = () => {
      const d = Math.round(el.clientWidth);
      const next = Math.max(200, Math.min(360, d));
      setVpD(next);
      setOffset((o) => clampOffset(o, next));
    };
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [cropOpen, clampOffset]);

  // Cleanup crop dialog
  const handleCloseCrop = () => {
    setCropOpen(false);
    if (rawObjectUrl) URL.revokeObjectURL(rawObjectUrl);
    setRawObjectUrl(null);
    setImgEl(null);
    setImgNatural(null);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    setLastPickedFile(null);
    setIsDragging(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  // Mouse drag
  const onMouseDown: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!imgNatural) return;
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      startOffsetX: offset.x,
      startOffsetY: offset.y,
    };
    setIsDragging(true);
  };
  const onMouseMove: React.MouseEventHandler<HTMLDivElement> = (e) => {
    if (!dragRef.current) return;
    const dx = e.clientX - dragRef.current.startX;
    const dy = e.clientY - dragRef.current.startY;
    setOffset(
      clampOffset(
        {
          x: dragRef.current.startOffsetX + dx,
          y: dragRef.current.startOffsetY + dy,
        },
        vpD
      )
    );
  };
  const onMouseUp = () => {
    dragRef.current = null;
    setIsDragging(false);
  };

  // Wheel zoom
  const onWheel: React.WheelEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
    const next = clampZoom(zoom + (e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP));
    setZoom(next);
    setOffset((o) => clampOffset(o, vpD));
  };

  // Touch: drag/pinch
  const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (e.touches.length === 1) {
      lastTouches.current = {
        ox: e.touches[0].clientX,
        oy: e.touches[0].clientY,
      };
      setIsDragging(true);
    } else if (e.touches.length >= 2) {
      lastTouches.current = { dist: distance(e.touches[0], e.touches[1]) };
      setIsDragging(false);
    }
  };
  const onTouchMove: React.TouchEventHandler<HTMLDivElement> = (e) => {
    if (!lastTouches.current) return;
    if (e.touches.length === 1 && lastTouches.current.ox !== undefined) {
      const dx = e.touches[0].clientX - (lastTouches.current.ox as number);
      const dy = e.touches[0].clientY - (lastTouches.current.oy as number);
      setOffset((o) => clampOffset({ x: o.x + dx, y: o.y + dy }, vpD));
      lastTouches.current.ox = e.touches[0].clientX;
      lastTouches.current.oy = e.touches[0].clientY;
    } else if (
      e.touches.length >= 2 &&
      lastTouches.current.dist !== undefined
    ) {
      const d = distance(e.touches[0], e.touches[1]);
      const diff = d - lastTouches.current.dist!;
      const next = clampZoom(zoom + diff / 200);
      setZoom(next);
      setOffset((o) => clampOffset(o, vpD));
      lastTouches.current.dist = d;
    }
  };
  const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = () => {
    lastTouches.current = null;
    setIsDragging(false);
  };

  // Save crop → canvas → File
  const handleSaveCrop = async () => {
    if (!imgEl || !imgNatural || !lastPickedFile) return;

    const outW = Math.round(exportSize);
    const outH = Math.round(outW / (aspect || 1));
    const canvas = document.createElement("canvas");
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext("2d")!;

    const { w: iw, h: ih } = imgNatural;
    const baseScale = Math.max(vpD / iw, vpD / ih);
    const totalScale = baseScale * zoom;
    const dispW = iw * totalScale;
    const dispH = ih * totalScale;

    const imgLeft = (vpD - dispW) / 2 + offset.x;
    const imgTop = (vpD - dispH) / 2 + offset.y;

    const srcX = Math.max(0, -imgLeft / totalScale);
    const srcY = Math.max(0, -imgTop / totalScale);
    const srcW = Math.min(iw - srcX, vpD / totalScale);
    const srcH = Math.min(ih - srcY, vpD / totalScale);

    ctx.clearRect(0, 0, outW, outH);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(imgEl, srcX, srcY, srcW, srcH, 0, 0, outW, outH);

    const type = outputType || lastPickedFile.type || "image/jpeg";
    const blob: Blob = await new Promise((res) =>
      canvas.toBlob(
        (b) => res(b as Blob),
        type,
        type === "image/jpeg" ? jpegQuality : undefined
      )
    );
    const croppedFile = new File([blob], lastPickedFile.name, { type });

    onChange?.(croppedFile);
    setCleared(false);
    handleCloseCrop();
    onSave?.(croppedFile);
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <>
      <Stack alignItems="center" spacing={1.5}>
        {title && (
          <Typography
            variant={titleVariant}
            align={titleAlign}
            sx={{ width: "100%" }}
          >
            {title}
          </Typography>
        )}

        <Box
          onClick={handlePick}
          role="button"
          aria-label="Upload Image"
          tabIndex={0}
          onKeyDown={(e) =>
            (e.key === "Enter" || e.key === " ") && handlePick()
          }
          sx={(theme) => ({
            width: size,
            height: size,
            borderRadius: br,
            outline: "none",
            position: "relative",
            cursor: disabled ? "not-allowed" : "pointer",
            border: showBorder
              ? `1px dashed ${alpha(theme.palette.text.primary, 0.5)}`
              : "none",
            background: preview
              ? `center/cover no-repeat url(${preview})`
              : alpha(theme.palette.text.primary, 0.04),
            display: "grid",
            placeItems: "center",
            transition: "box-shadow .2s ease, transform .06s ease",
            "&:hover": !disabled
              ? {
                  boxShadow: `0 0 0 4px ${alpha(
                    theme.palette.primary.main,
                    0.15,
                  )}`,
                  "& .remove-btn": { opacity: 1 },
                  "& .default-icon": { display: "none" },
                  "& .hover-icon": { display: "block" },
                }
              : undefined,
            "&:active": !disabled ? { transform: "scale(0.99)" } : undefined,
          })}
        >
          {!preview && (
            <Stack alignItems="center" spacing={1} sx={{ userSelect: "none" }}>
              {shape === "circle" ? (
                <CameraAlt fontSize="large" />
              ) : (
                <>
                  <PermIdentityOutlined
                    fontSize="large"
                    className="default-icon"
                  />
                  <CameraAlt
                    fontSize="large"
                    className="hover-icon"
                    sx={{ display: "none" }}
                  />
                </>
              )}
              {shape === "circle" && (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  Upload photo
                </Typography>
              )}
            </Stack>
          )}

          {!!preview && !disabled && (
            <IconButton
              color="primary"
              className="remove-btn"
              size="small"
              aria-label="Remove selected image"
              onClick={(e) => {
                e.stopPropagation();
                if (preventImmediateClearOnRemove) {
                  onRemove?.();
                } else {
                  clear();
                }
              }}
              sx={{
                position: "absolute",
                top: shape === "circle" ? 6 : -10,
                right: shape === "circle" ? 6 : -10,
                backgroundColor: colors.error[300],
                boxShadow: 1,
                opacity: 0,
                transition: "opacity 0.2s ease",
                "&:hover": { backgroundColor: colors.error[400] },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          )}

          <input
            ref={inputRef}
            id={id}
            name={name}
            type="file"
            accept={acceptAttr}
            onChange={handleChange}
            hidden
            disabled={disabled}
          />
        </Box>

        {!hideHelper && (
          <Stack spacing={0} alignItems="center">
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Allowed *.jpeg, *.jpg, *.png
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              max size of {maxSizeMB} Mb
            </Typography>
          </Stack>
        )}

        {(errorText || internalError) && (
          <Typography variant="caption" color="error">
            {errorText || internalError}
          </Typography>
        )}
      </Stack>

      {/* Crop dialog */}
      <Dialog
        open={cropOpen}
        onClose={handleCloseCrop}
        maxWidth="xs"
        PaperProps={{
          sx: {
            ...paperSx(false),
            width: 420,
            maxWidth: "calc(100vw - 32px)",
          },
        }}
      >
        {/* Header */}
        <Box sx={headerSx}>
          <Box sx={iconBoxSx(modalTokens.accent)}>
            <ImageIcon size={18} color={modalTokens.accent} />
          </Box>

          <Box>
            <Typography
              variant="h6"
              fontWeight={700}
              sx={{ color: modalTokens.textPrimary }}
            >
              Adjust your photo
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: modalTokens.textSecondary, mt: 0.5 }}
            >
              Crop and adjust photo
            </Typography>
          </Box>
        </Box>

        {/* Body */}
        <DialogContent
          sx={{
            ...bodySx,
            pt: 3,
            display: "flex",
            flexDirection: "column",
            gap: 3,
          }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <Box
              ref={viewportRef}
              onMouseDown={onMouseDown}
              onMouseMove={onMouseMove}
              onMouseUp={onMouseUp}
              onMouseLeave={onMouseUp}
              onWheel={onWheel}
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
              sx={{
                width: "min(88vw, 360px)",
                height: vpD,
                borderRadius: br,
                position: "relative",
                overflow: "hidden",
                background: "#050816",
                border: `1px solid ${modalTokens.border}`,
                touchAction: "none",
                cursor: imgEl ? (isDragging ? "grabbing" : "grab") : "default",
                userSelect: "none",
              }}
            >
              {imgEl && imgNatural && rawObjectUrl && (
                <Box
                  component="img"
                  src={rawObjectUrl}
                  alt="Crop preview"
                  draggable={false}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: (() => {
                      const baseScale = Math.max(
                        vpD / imgNatural.w,
                        vpD / imgNatural.h,
                      );
                      const s = baseScale * zoom;

                      return `translate(-50%, -50%) translate(${offset.x}px, ${offset.y}px) scale(${s})`;
                    })(),
                    userSelect: "none",
                    pointerEvents: "none",
                  }}
                />
              )}

              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  boxShadow: "inset 0 0 0 2px rgba(255,255,255,.12)",
                  borderRadius: br,
                  pointerEvents: "none",
                }}
              />
            </Box>
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ width: "100%" }}
          >
            <RemoveRoundedIcon sx={{ color: modalTokens.textSecondary }} />

            <Slider
              min={1}
              max={5}
              step={0.01}
              value={zoom}
              onChange={(_, v) => {
                const next = clampZoom(v as number);
                setZoom(next);
                setOffset((o) => clampOffset(o, vpD));
              }}
              sx={{
                color: modalTokens.accent,
                flex: 1,
                "& .MuiSlider-rail": {
                  opacity: 1,
                  background: "rgba(255,255,255,.12)",
                },
                "& .MuiSlider-thumb": {
                  width: 18,
                  height: 18,
                },
              }}
            />

            <AddRoundedIcon sx={{ color: modalTokens.textSecondary }} />
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            display: "flex",
            gap: 1.5,
          }}
        >
          <Button
            fullWidth
            variant="contained"
            onClick={handleSaveCrop}
            sx={{
              ...primaryBtnSx,
              flex: 1,
              "& .MuiTypography-root": {
                color: "#ffffff",
              },
            }}
          >
            Save
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCloseCrop}
            sx={{
              ...cancelBtnSx,
              flex: 1,
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProfileImageUpload;
